import { defineStore } from 'pinia';
import { ref } from 'vue';

/** Lien d'un toast (lot T : « Voir dans À faire » après une action directe qui change des cartes). */
export interface ToastLink {
  label: string;
  to: string;
}

/** Lot BI : bouton d'un toast (« Annuler » pendant le délai d'une validation). */
export interface ToastAction {
  label: string;
  run: () => void;
}

/** Lot BI : compte à rebours affiché à côté du bouton (« 5 s » et une barre qui se vide). */
export interface ToastCountdown {
  endsAt: number;
  totalMs: number;
}

export interface ToastOptions {
  action?: ToastAction;
  countdownMs?: number;
}

/**
 * Message bref en bas de l'écran (comme le « toast » de la maquette), avec un lien ou un bouton facultatifs.
 * `show` rend un numéro : `hide(numéro)` ne cache que ce toast-là (pas un message affiché depuis).
 */
export const useToastStore = defineStore('toast', () => {
  const message = ref('');
  const visible = ref(false);
  const link = ref<ToastLink | null>(null);
  const action = ref<ToastAction | null>(null);
  const countdown = ref<ToastCountdown | null>(null);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let ticket = 0;

  function show(
    text: string,
    durationMs = 3200,
    withLink: ToastLink | null = null,
    options: ToastOptions = {},
  ): number {
    ticket += 1;
    message.value = text;
    link.value = withLink;
    action.value = options.action ?? null;
    countdown.value = options.countdownMs
      ? { endsAt: Date.now() + options.countdownMs, totalMs: options.countdownMs }
      : null;
    visible.value = true;
    clearTimeout(timer);
    timer = setTimeout(() => {
      visible.value = false;
      action.value = null;
      countdown.value = null;
    }, durationMs);
    return ticket;
  }

  function hide(only?: number): void {
    if (only !== undefined && only !== ticket) return;
    clearTimeout(timer);
    visible.value = false;
    action.value = null;
    countdown.value = null;
  }

  return { message, visible, link, action, countdown, show, hide };
});
